/**
 * Acceptation d'une invitation, depuis le lien recu par e-mail.
 *
 * Cette page MANQUAIT entierement (constate le 15/09/2026) : le backend
 * envoyait bien un lien vers `/invitations/accept?token=…`, mais aucune
 * page ne repondait a cette adresse. L'invite atterrissait sur l'ecran de
 * connexion, sans mot de passe et sans moyen d'accepter. Les invitations
 * n'avaient donc jamais fonctionne de bout en bout, pour aucun client -
 * exactement le defaut deja trouve le 30/08 avec la reinitialisation.
 *
 * Un second obstacle attendait l'invite APRES l'acceptation : l'ecran de
 * connexion exige l'identifiant de l'organisation, que l'e-mail ne donne
 * pas. La route d'acceptation le renvoie desormais, et on le memorise pour
 * pre-remplir la connexion.
 */
import { useState } from "react";

import { session } from "../session";

/** Le backend impose 12 caracteres (AcceptInvitationRequest), comme pour
 *  la reinitialisation. On le verifie ici aussi, pour le dire AVANT
 *  l'envoi. */
const LONGUEUR_MINIMALE = 12;

const CONSOLE = "https://admin.sign365.io/";

interface Acceptation {
  tenant_slug: string;
  tenant_name: string;
  console: boolean;
}

/** Un message par cas, ecrit pour l'invite. Surtout pas un message
 *  generique : c'est un libelle generique qui affichait « session
 *  expiree » sur un simple mot de passe errone, le 30/08. */
function messageEchec(statut: number, detail: string): string {
  if (statut === 404) {
    return "Ce lien d'invitation n'est pas reconnu. Ouvrez-le tel qu'il figure dans l'e-mail, en entier et sans le modifier.";
  }
  if (statut === 410 && /utilis/i.test(detail)) {
    return "Cette invitation a déjà été acceptée. Connectez-vous avec le mot de passe choisi à ce moment-là.";
  }
  if (statut === 410) {
    return "Cette invitation a expiré. Demandez à la personne qui vous a invité de vous en envoyer une nouvelle.";
  }
  if (statut === 422) {
    return `Le mot de passe doit contenir au moins ${LONGUEUR_MINIMALE} caractères.`;
  }
  // NV-03 (audit du 02/10/2026) : un compte qui a deja un mot de passe le
  // garde ; il le saisit pour rejoindre ce nouvel espace.
  if (statut === 403) {
    return "Vous avez déjà un compte Sign365 avec cette adresse. Saisissez votre mot de passe actuel dans les deux champs pour rejoindre cet espace.";
  }
  return "Une erreur est survenue. Réessayez dans un instant.";
}

export function InvitationAccept({ onTermine }: { onTermine: () => void }) {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [echec, setEchec] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState<Acceptation | null>(null);

  const identiques = motDePasse === confirmation;
  const assezLong = motDePasse.length >= LONGUEUR_MINIMALE;

  const soumettre = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnvoi(true);
    setEchec(null);
    try {
      const reponse = await fetch("/api/tenants/invitations/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password: motDePasse,
          given_name: prenom.trim() || null,
          family_name: nom.trim() || null,
        }),
      });
      if (!reponse.ok) {
        let detail = "";
        try {
          const corps = await reponse.json();
          detail = typeof corps?.detail === "string" ? corps.detail : "";
        } catch {
          /* corps non-JSON */
        }
        setEchec(messageEchec(reponse.status, detail));
        return;
      }
      const acceptation = (await reponse.json()) as Acceptation;
      // L'identifiant n'est pas un secret : on le memorise pour que
      // l'ecran de connexion soit deja rempli.
      if (!acceptation.console) session.memoriserOrganisation(acceptation.tenant_slug);
      setResultat(acceptation);
    } catch {
      setEchec(messageEchec(0, ""));
    } finally {
      setEnvoi(false);
    }
  };

  if (!token) {
    return (
      <div className="centered" style={{ padding: "2rem 1rem" }}>
        <div className="card stack" style={{ width: "min(28rem, 100%)" }}>
          <h1 className="card__title">Lien incomplet</h1>
          <p>
            Cette adresse ne contient pas de jeton d'invitation. Ouvrez le lien tel qu'il
            figure dans l'e-mail, sans le modifier.
          </p>
          <div className="actions" style={{ marginTop: 0 }}>
            <button type="button" className="btn btn--secondary" onClick={onTermine}>
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (resultat) {
    return (
      <div className="centered" style={{ padding: "2rem 1rem" }}>
        <div className="card stack" style={{ width: "min(28rem, 100%)" }}>
          <div className="alert alert--success" role="status">
            <strong>Votre compte est créé.</strong>
          </div>

          {resultat.console ? (
            <>
              <p>
                Vous êtes administrateur de la plateforme Sign365. Votre espace est la
                <strong> Admin</strong> (administration de la plateforme), pas le portail client.
              </p>
              <p className="muted">
                À votre première connexion, l'Admin vous demandera d'activer la double
                authentification. Configurez-la de préférence sur un autre appareil que celui
                d'un autre administrateur : c'est ce qui permet de ne jamais perdre l'accès.
              </p>
              <div className="actions" style={{ marginTop: 0 }}>
                <a className="btn btn--primary" href={CONSOLE}>
                  Ouvrir l'Admin
                </a>
              </div>
            </>
          ) : (
            <>
              <p>
                Vous avez rejoint <strong>{resultat.tenant_name}</strong>.
              </p>
              <p className="muted">
                Identifiant de votre organisation, à indiquer à la connexion :{" "}
                <strong>{resultat.tenant_slug}</strong>. Il est déjà pré-rempli.
              </p>
              <div className="actions" style={{ marginTop: 0 }}>
                <button type="button" className="btn btn--primary" onClick={onTermine}>
                  Se connecter
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="centered" style={{ padding: "2rem 1rem" }}>
      <form className="card stack" style={{ width: "min(28rem, 100%)" }} onSubmit={soumettre}>
        <div>
          <h1 className="card__title" style={{ marginBottom: "0.15rem" }}>
            Accepter l'invitation
          </h1>
          <p className="card__lead" style={{ marginBottom: 0 }}>
            Choisissez le mot de passe de votre compte Sign365.
          </p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="prenom">
            Prénom <span className="muted">(facultatif)</span>
          </label>
          <input
            id="prenom"
            className="input"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
            autoComplete="given-name"
            autoFocus
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="nom">
            Nom <span className="muted">(facultatif)</span>
          </label>
          <input
            id="nom"
            className="input"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            autoComplete="family-name"
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="mdp">
            Mot de passe
          </label>
          <input
            id="mdp"
            className="input"
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete="new-password"
            required
          />
          <p className="muted" style={{ marginTop: "0.3rem" }}>
            {LONGUEUR_MINIMALE} caractères minimum. Une phrase dont vous vous souvenez vaut
            mieux qu'une suite de symboles que vous devrez noter quelque part.
          </p>
        </div>

        <div className="field">
          <label className="field-label" htmlFor="conf">
            Confirmation
          </label>
          <input
            id="conf"
            className="input"
            type="password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="new-password"
            aria-invalid={confirmation !== "" && !identiques ? "true" : undefined}
            required
          />
          {confirmation !== "" && !identiques && (
            <p className="muted" style={{ marginTop: "0.3rem", color: "var(--red-dark)" }}>
              Les deux saisies diffèrent.
            </p>
          )}
        </div>

        {echec && (
          <div className="alert alert--error" role="alert">
            {echec}
          </div>
        )}

        <div className="actions" style={{ marginTop: 0 }}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={envoi || !assezLong || !identiques}
          >
            Créer mon compte
          </button>
        </div>
      </form>
    </div>
  );
}
